import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { colors, css, fonts, frames, glyphs } from '../../../../Styles';
import { PageHeader, ProgressBar, NewButtonList, NewButton, FilterDisplayList, NewInput, CheckBox, CheckStyles, Overlay, TableLayout, TableTypes } from "../../../../Components";
import { exportTableToCsv } from "./_Reports/exportTableToCsv";
import { exportTableToExcel } from "./_Reports/exportTableToXml";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types, LocaleFarmDate, NewInputHandles, NewFieldStyles, ReportData, NewFieldTypes } from "../../../../Definitions";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { GenericDataParameters, Units, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@apollo/client";
import { RootState, useAppSelector } from "../../../../Store";
import { Graph_QL, useAnimal, useReport } from "../../../../Services";
import { FeedReportAnimalData, FeedReportGroupData, FeedReportSystemData, GET_ARCHIVED_REPORTS, ReportWithData } from "../../../../Services/Agrisys_GqlDataCaller/ReportDataCalls/GQLCalls/getArchivedReportsCall";
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/root";
import { cloneDeep } from "lodash";
import FeedLabel from "../../../../Components/ProgressViewer/SubComponents/feedLabel";
import { animalDataAdapter } from "../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../../../Components/_Components";
import ConfirmModal from "../../../../Components/GlobalModals/confirmModal";
import { useDispatch, useSelector } from "react-redux";
import { SET_EDIT_MODE } from "../../../../Store/UI/editModeSlice";

const ignoredKeys = ['consumption', "feedcurve_milestones", "__typename", "cycle", "condition", "animal_type", "animal_number"]

function OverFed({ header }: { header: string }) {
  return (
    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "5px", overflow: "hidden" }}>
      <div style={{ width: "130px", flexShrink: "0" }}>{header}</div>
      <FeedLabel cursor="pointer" tooltip />
    </div>
  )
}

export default function Report() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigator = useNavigate();
  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);

  const reportId = location.state?.params.selected;
  const [selectedReport, setReport] = useState<number | undefined>(reportId);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false);
  // const [editTable, setEditTable] = useState(false);

  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const groupOptions = useGroupOptions(UndefinedOptions.undefined);
  const systemOptions = useSystemOptions();

  useEffect(() => {
    setGlobalFilterValue(searchTerm);
  }, [searchTerm]);

  // #region | GraphQl
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { data: archivedReportState, call: getArchivedReportsCall, maintainEntries: updateReport } = useReport(connection);
  const { call: getAnimals } = useAnimal(connection);
  const { loading, error, data } = useQuery<ReportWithData>(GET_ARCHIVED_REPORTS, { variables: { domain: farm, input: { ids: selectedReport, offset: 0, fetchAmount: 1 }, ifFetchData: true }, skip: selectedReport === undefined })

  useEffect(() => {
    if (!archivedReportState[domain]?.data) {
      getArchivedReportsCall({ offset: 0, fetchAmount: 50, ifFetchData: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const archivedReports = useMemo(() => {
    if (archivedReportState && archivedReportState[domain]) {
      return archivedReportState[domain].data.map((report) => ({ label: report.name ?? "", id: report.reportId }));
    }
    return [];
  }, [archivedReportState, domain])

  const report = useMemo<(ReportData & { data: (FeedReportAnimalData | FeedReportGroupData | FeedReportSystemData)[]; }) | undefined>(() => {
    if (selectedReport !== undefined && data && data.report.archived[0] && data.report.archived[0].reportId === selectedReport && "data" in data.report.archived[0] && Array.isArray(data.report.archived[0].data)) {
      const reportData = data.report.archived[0].data;
      return { ...data.report.archived[0], data: reportData };
    }
  }, [data, selectedReport])
  // #endregion

  const animalEntities = useAppSelector((state) => animalDataAdapter.getSelectors().selectEntities(state.graphQlData.animals));
  const animalsInReport = useMemo(() => {
    const animalIds = report?.data.map((data) => (data as any)?.animal_id).filter((value) => value !== undefined);
    if (animalIds && animalIds.length) {
      return animalIds.map((id) => animalEntities[id]).filter(Boolean);
    }
  }, [animalEntities, report?.data])

  useEffect(() => {
    const animalIds = report?.data.map((data) => (data as any)?.animal_id).filter((value) => value !== undefined);

    if (animalIds && animalIds.length && animalsInReport?.length !== animalIds.length) {
      getAnimals({ ifFeedCurveInfo: false, ifFeedCurveBaseInfoOnly: true, ifSkipConsumption: true, ifSkipLastTenDaysConsumption: true, ids: animalIds })
    }
  }, [animalsInReport?.length, getAnimals, report?.data])

  useEffect(() => {
    if (!selectedReport) {
      if (location.state?.params?.selected) {
        setReport(location.state.params.selected);
      } else if (archivedReports.length) {
        setReport(archivedReports[0].id);
      }
    }
  }, [archivedReports, location.state?.params.selected, selectedReport])

  // #region | Table Construction
  const { accessorData, tableData, consumptionAccessors } = useMemo(() => {
    const accessorMap = new Map<string, GenericDataParameters<any>>()
    const consumptionAccessors = new Set<string>()
    if (!report) {
      return { accessorData: [], tableData: [], consumptionAccessors: new Set<string>() }
    }
    if (report.dataLevel === 1) {

      accessorMap.set("animal_button", {
        headerValue: t("params.animalId", { defaultValue: "animal no." }),
        accessor: "animal_button",
        // type: ListData_ValueTypes.Button,
        type: ListData_ValueTypes.String, // TODO should be button?
        width: "150px",
        sort: true,
        filter: true,
      });
    }

    if (report.dataLevel === 2) {
      accessorMap.set("group_name", {
        headerValue: t("params.group", { defaultValue: "group name" }),
        accessor: "group_name",
        type: ListData_ValueTypes.String,
        sort: true,
        styleOverrideOnValue: false,
        width: "200px",
        filter: true,
        options: groupOptions
      });
    }

    if (report.dataLevel === 3) {
      accessorMap.set("system_name", {
        headerValue: t("params.system", { defaultValue: "system name" }),
        accessor: "system_name",
        type: ListData_ValueTypes.String,
        sort: true,
        styleOverrideOnValue: false,
        width: "200px",
        filter: true,
        options: systemOptions
      });
    }

    const tableData = cloneDeep(report.data).map((reportRow) => {
      Object.keys(reportRow).forEach((key) => {
        if (accessorMap.get(key) || key.includes("_id") || ignoredKeys.includes(key)) {
          return
        }
        accessorMap.set(key, {
          headerValue: t(`reportPages.reportColumn.${key}`, { defaultValue: key.replace(/_/g, " ") }),
          accessor: key,
          type: ListData_ValueTypes.String,
          sort: true,
          filter: true,
          styleOverrideOnValue: false,
        })
      })

      if ("birthday" in reportRow && reportRow.birthday && typeof reportRow.birthday === "string") {
        reportRow["birthday"] = new LocaleFarmDate(reportRow["birthday"]).toDateForDisplay()
      }
      if ("milestone_day" in reportRow && reportRow.milestone_day && typeof reportRow.milestone_day === "string") {
        reportRow["milestone_day"] = new LocaleFarmDate(reportRow["milestone_day"]).toDateForDisplay()
      }

      const extraFields: { [key: string]: any } = {};

      if ("animal_number" in reportRow && typeof reportRow.animal_number === "number") {
        const animalId = reportRow.animal_number;
        const animal = reportRow.animal_id && typeof reportRow.animal_id === "number" ? animalEntities[reportRow.animal_id] : undefined;
        extraFields["animal_button"] = (
          <NewButton
            content={String(animalId)}
            buttonType={ButtonTypes.textButton}
            startEnhancer={animal === undefined || animal?.deleted ? <div style={{ width: "20px" }}></div> : <glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary} />}
            disabled={animal?.deleted}
            onClick={() =>
              RootNavigation({
                Navigator: navigator,
                navigation_page: Pages.animals,
                navigation_tab: Pages.animal,
                replace: true,
                navArgs: { selected: reportRow.animal_id },
                currentDomain: farm,
              })
            }
            overrides={{
              contentStyles: { color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph },
              startEnhancerStyles: { marginRight: "15px" },
            }}
          />
        );
      }

      const reportRowConsumptionData: { [key: string]: JSX.Element } = {}
      const consumptionAcc = { total: 0, progressed: 0 }
      if ("consumption" in reportRow && Array.isArray(reportRow.consumption)) {
        reportRow.consumption.forEach((consumptionRow) => {
          if (consumptionRow && typeof consumptionRow === "object" && "timestamp" in consumptionRow) {
            consumptionAccessors.add(String(consumptionRow.timestamp));
            accessorMap.set(String(consumptionRow.timestamp), {
              headerValue: <OverFed header={`${t("reportPages.reportColumn.consumed", { defaultValue: "consumed" })} ${new LocaleFarmDate(consumptionRow.timestamp).toDateForDisplay()}`} />,
              accessor: String(consumptionRow.timestamp),
              type: ListData_ValueTypes.ProgressBar,
              sort: true,
              styleOverrideOnValue: false,
              width: "220px",
              unit: Units.kg,
              filter: true,
            })

            const consumptionTotal = +(consumptionRow.expected_amount);
            const consumptionProgressed = +(consumptionTotal - (consumptionRow.accumulated_amount));
            consumptionAcc.total = consumptionAcc.total + consumptionRow.expected_amount;
            consumptionAcc.progressed = consumptionAcc.progressed + consumptionRow.expected_amount - consumptionRow.accumulated_amount;
            reportRowConsumptionData[String(consumptionRow.timestamp)] = <ProgressBar barWidth={"130px"} total={consumptionTotal} progressed={consumptionProgressed} extension={{ hasExtension: true }} />
          }
        });
        accessorMap.set("totalPeriodConsumption", {
          headerValue: <OverFed header={t(`reportPages.reportColumn.totalPeriodConsumption`, { defaultValue: 'Total period consumption' })} />,
          accessor: 'totalPeriodConsumption',
          type: ListData_ValueTypes.ProgressBar,
          sort: true,
          width: "220px",
          unit: Units.kg,
          filter: true,
        })
      }
      reportRowConsumptionData['totalPeriodConsumption'] = <ProgressBar barWidth={"130px"} total={+(consumptionAcc.total)} progressed={+(consumptionAcc.progressed)} extension={{ hasExtension: true }} />

      return { ...reportRow, ...reportRowConsumptionData, ...extraFields }
    })
    return { accessorData: Array.from(accessorMap.values()), tableData, consumptionAccessors };
  }, [report, t, animalEntities, navigator, farm]);

  const columns = useColumnConstructor({ accessorData, tableStyle: TableStyles_Types.primaryTable, canEdit: false })
  const table = useReactTable({
    columns: columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: globalFilter,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnPinning: { left: ["animal_button", "group_name", "system_name"] },
      columnOrder: [
        "animal_button",
        "group_name",
        "system_name",
        "totalPeriodConsumption",
        ...(Array.from(consumptionAccessors).sort((stringTimestampA, stringTimestampB) => (new Date(stringTimestampA).getTime() - new Date(stringTimestampB).getTime()))),
        "animal_group_name",
        "milestone_name"
      ]
    },
  })
  // #endregion

  if (error) {
    return <>{"An error occurred when attempting to load the report data"}</>
  }

  // if ((!selectedReport) ) {
  //   return "loading";
  // }
  const consumptionReportLabel = t("report.consumption_report", { defaultValue: "consumption_report" });

  const currentIndex = archivedReports.findIndex(
    (report) => report.id === selectedReport
  );

  const handleNext = () => {
    if (!selectedReport || currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % archivedReports.length;
    const nextReportId = archivedReports[nextIndex].id;
    setReport(nextReportId);
  };

  const handlePrev = () => {
    if (!selectedReport || currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + archivedReports.length) % archivedReports.length;
    const prevReportId = archivedReports[prevIndex].id;
    setReport(prevReportId);
  };

  // #region | Button List
  const useContents = (): [string[], JSX.Element[], (() => void)[]] => {
    const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];

    if (editMode) {
      buttonEntries.push(
        // { label: t("buttons.save", { defaultValue: "save" }), glyphs: glyphs.Check({}), onClick: () => { setEditTable(false) }}, // TODO: Save table preferences
        { label: t("buttons.cancel", { defaultValue: "cancel" }), glyphs: glyphs.Close({}), onClick: () => { dispatchEditMode(SET_EDIT_MODE(false)) } }
      )
    }

    buttonEntries.push(
      { label: t("buttons.edit", { defaultValue: "edit" }), glyphs: glyphs.Edit({}), onClick: () => dispatchEditMode(SET_EDIT_MODE(true)) },
      { label: t("buttons.excel", { defaultValue: "excel" }), glyphs: glyphs.Excel({}), onClick: () => exportTableToExcel(tableData, `${consumptionReportLabel}.xlsx`, consumptionReportLabel, accessorData) },
      { label: t("buttons.csv", { defaultValue: "csv" }), glyphs: glyphs.Csv({}), onClick: () => exportTableToCsv(tableData, `${consumptionReportLabel}.csv`, accessorData) }
    )

    const labels = buttonEntries.map((b) => b.label);
    const icons = buttonEntries.map((b) => b.glyphs)
    const onclicks = buttonEntries.map((b) => b.onClick);
    return [labels, icons, onclicks];
  };

  const ButtonRow = memo(({ editMode }: { editMode: boolean }) => {
    const [contentsRes, icons, onclicks] = useContents();

    return (
      <NewButtonList
        contents={contentsRes}
        onClick={(i) => { onclicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editMode ? [false, true, true, true] : [false, false, false]}
        overrides={(index) => ({
          baseStyles: { backgroundColor: editMode && index === 1 ? colors.greyPrimary : colors.greySecondary },
          disabledStyles: { opacity: editMode && index > 1 ? 0.5 : 1 },
        })}
      />
    );
  });
  // #endregion

  return (
    <frames.Page_inner>
      {editMode ? <Overlay beforeUnload onClickAlert /> : <></>}

      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.report}`, { defaultValue: "report" })}
        subSelect={{
          onNext: handleNext,
          onPrev: handlePrev,
          options: archivedReports,
          onChange: (e) => { setReport(e?.id ?? location.state.params.selected) },
          defaultValue: archivedReports.find(report => report.id === selectedReport),
          disabled: archivedReports.length === 0 || archivedReports === undefined ? true : false
        }}
        optionButtons={{
          contents: [t("reportPages.report.deleteReport", { defaultValue: "Delete Report" })],
          onClick: (i) => { setConfirmDeleteModal(true) },
          disabled: archivedReports.length === 0 || archivedReports === undefined || selectedReport === undefined ? true : false
        }}
      />
      <frames._page_inner_content>
        {archivedReports.length === 0 || !archivedReports ? <p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>{t("reportPages.report.noReports", { defaultValue: "You have no archived reports" })}</p>
          : selectedReport === undefined ? <p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>{t("reportPages.report.noReportSelected", { defaultValue: "No report selected" })}</p>
            : <TableLayout
              editableTable
              zIndex={editMode ? 999 : 0}
              tableType={TableTypes.fitContent}
              searchbBarArea={
                <NewInput ref={searchRef}
                  startEnhancer={glyphs.Search({})}
                  onChange={(value: string) => { setSearchTerm(value) }}
                  fieldOverrides={{ fieldStyle: NewFieldStyles.greySecondary }}
                  placeholder={t("search", { defaultValue: "search" })}
                  isClearable
                />
              }
              filterDisplayListArea={
                <FilterDisplayList filters={columnFilters} setColumnFilters={setColumnFilters} />
              }
              buttonArea={<>
                {/* <FilterPopup<GenericRow> accessorData={accessorData} table={table} /> */}
                <ButtonRow editMode={editMode} />
              </>}
              tableArea={
                <ReactiveTable minRows={30} loading={loading || (report?.dataLevel === 1 && animalsInReport?.length !== report?.data.map((data) => (data as any)?.animal_id).length)} table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("report.table.emptyText", { defaultValue: "No report data" })} />
              }
              additionalArea={editMode ? <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px" }}>
                  <p style={{ ...fonts.fontSize4, ...fonts.paragraph, ...fonts.fontWeight2, color: colors.whitePrimary, borderBottom: "2px solid " + colors.greySecondary + 80 }}>{t("animalPages.animals.columns", { defaultValue: "Columns" })}</p>
                  {/* <p style={{ ...fonts.fontSize4, ...fonts.paragraph, ...fonts.fontWeight2, color: colors.whitePrimary, borderBottom: "2px solid "+colors.greySecondary+80 }}>{t("animalPages.animals.priority", { defaultValue: "Priority" })}</p> */}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {table.getAllColumns().map((col) => {
                    let checkboxText = t(`reportPages.reportColumn.${col.id}`, { defaultValue: col.id.replace(/_/g, " ") });

                    // totalPeriodConsumption column
                    if (col.id === "totalPeriodConsumption") {
                      checkboxText = t("reportPages.reportColumn.totalPeriodConsumption", { defaultValue: "Total period consumption" });
                    }

                    // Consumption column
                    if (consumptionAccessors.has(col.id)) {
                      const isoDateStr = col.id;
                      const date = new Date(isoDateStr);
                      if (!isNaN(date.getTime())) {
                        const dateStr = new LocaleFarmDate(date).toDateForDisplay();
                        const consumedText = t("reportPages.reportColumn.consumed", { defaultValue: "consumed" });
                        checkboxText = `${consumedText} ${dateStr}`;
                      }
                    }

                    return (
                      <div key={col.id} style={{ display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px" }}>
                        <CheckBox
                          text={checkboxText}
                          disabled={!col.getCanHide()}
                          isChecked={col.getIsVisible()}
                          onChange={(checked: boolean) => col.toggleVisibility(checked)}
                          checkStyle={CheckStyles.greyPrimary}
                        />
                        {/* <NewInput
                      fieldOverrides={{
                        fieldType: NewFieldTypes.tertiaryField,
                        fieldStyle: NewFieldStyles.greySecondary,
                        fieldWidth: "60px"
                      }}
                      isDisabled={!col.getIsVisible()}
                      valueOverrides={{ valueType: "number" }}
                    /> */}
                      </div>
                    )
                  })}
                </div>
              </div> : undefined}
            />
        }
      </frames._page_inner_content>

      {confirmDeleteModal &&
        <ConfirmModal
          disclaimer={t("reportPages.report.deleteConfirm", { defaultValue: "Are you sure you want to delete this report?" })}
          onCancel={() => setConfirmDeleteModal(false)}
          onConfirm={() => { setConfirmDeleteModal(false); if (updateReport && report && report.reportId) { updateReport(undefined, { data: [report.reportId] }) } setReport(archivedReports[((archivedReports.findIndex(a => a.id === selectedReport)) + 1) % archivedReports.length].id) }}
        />
      }

    </frames.Page_inner>
  );
}
